import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore, unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { getNextOnboardingStep } from "@/lib/onboardingSteps";

const AuthContext = createContext(null);

// Session-scoped signup payload (email + password + username) — held only in
// memory until the OTP confirm step. We DO NOT persist the password to
// localStorage. Username is persisted separately so we can survive a page
// refresh (Xano now requires it on /account_verification/confirm).
const signupSessionStore = {
  _v: null,
  set(v) {
    this._v = v;
    sessionStorage.setItem(
      "brontoy_signup_pending",
      JSON.stringify({ email: v.email, username: v.username })
    );
  },
  get() { return this._v; },
  getEmail() {
    try {
      return JSON.parse(sessionStorage.getItem("brontoy_signup_pending") || "null")?.email || this._v?.email || null;
    } catch { return this._v?.email || null; }
  },
  getUsername() {
    try {
      return JSON.parse(sessionStorage.getItem("brontoy_signup_pending") || "null")?.username || this._v?.username || null;
    } catch { return this._v?.username || null; }
  },
  clear() { this._v = null; sessionStorage.removeItem("brontoy_signup_pending"); },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(!!tokenStore.get());
  // Welcome-splash gate — a small state machine that lets Login &
  // SuggestedPeople request a full-screen premium intro. It's rendered at
  // the App root so it survives route-guard redirects (PublicOnly kicks
  // the user off /login the moment they authenticate, so any component
  // mounted inside the auth pages would unmount before its animation
  // finishes).
  const [welcome, setWelcome] = useState(null); // null | { text, next }
  const triggerWelcome = useCallback((text, next) => {
    setWelcome({ text, next });
  }, []);
  const clearWelcome = useCallback(() => setWelcome(null), []);

  const fetchMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setOnboarding(null);
      setLoading(false);
      return null;
    }
    try {
      // /users-prod/me = social profile (avatar, counts). /profiles/me =
      // extended profile including DOB. /onboarding/status gates routing.
      // We prefer /users-prod/me for the current user shape but merge in
      // extra fields from /profiles/me when available.
      const [socialRes, profileRes, statusRes] = await Promise.all([
        api.get(EP.meUser).catch(() => null),
        api.get(EP.meProfile).catch(() => null),
        api.get(EP.onboardingStatus).catch(() => null),
      ]);
      const social = socialRes ? unwrap(socialRes.data) : null;
      const profile = profileRes ? unwrap(profileRes.data) : null;
      const statusData = statusRes ? unwrap(statusRes.data) : null;

      // Stale-token guard.
      //
      // If the token in localStorage was rejected by every /me call
      // (typically because it expired while the tab was closed), all
      // three unwrapped payloads come back null. Historically we still
      // left `authed=true` and set `onboarding=null`, and PublicOnly
      // then read `getNextOnboardingStep(null) === "basics"` and
      // slammed the user into `/onboarding/basics` instead of showing
      // the Landing page's Continue-with-Apple/Google/Email options.
      //
      // The clean recovery is to treat that state as "effectively
      // signed out": wipe the dead token, drop authed, and render `/`.
      if (!socialRes && !profileRes && !statusRes) {
        try {
          tokenStore.clearAll();
        } catch {
          /* storage disabled — ignore */
        }
        setAuthed(false);
        setUser(null);
        setOnboarding(null);
        return null;
      }

      const merged = {
        ...(profile?.user || profile?.profile || profile || {}),
        ...(social?.user || social || {}),
      };
      // Normalise avatar field so every component can read `.profile_photo_url`.
      // /users-prod/me returns `profile_photo`, /profiles/me returns
      // `profile_photo_url`, and joined responses use `profile_photo_media_id`.
      if (!merged.profile_photo_url && (merged.profile_photo || merged.avatar_url)) {
        merged.profile_photo_url = merged.profile_photo || merged.avatar_url;
      }
      // Ensure both spellings are present so downstream consumers that read
      // either field find a value.
      if (merged.profile_photo_url && !merged.profile_photo) {
        merged.profile_photo = merged.profile_photo_url;
      }

      setUser(Object.keys(merged).length ? merged : null);
      setOnboarding(statusData || null);
      return { user: merged, status: statusData };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Persist auth tokens to localStorage ONLY. Does NOT flip the `authed`
  // React state — that must stay false until we've fetched onboarding
  // status, otherwise `PublicOnly` briefly sees `authed=true` with a
  // stale `onboarding=null` and redirects to /onboarding/basics for a
  // frame (the "split-second profile-onboard screen flash" bug).
  const persistAuthTokens = (payload) => {
    if (!payload) return false;
    const token =
      payload.auth_token ||
      payload.authToken ||
      payload.token ||
      payload.accessToken;
    const refresh =
      payload.refresh_token ||
      payload.refreshToken;
    if (token) tokenStore.set(token);
    if (refresh) tokenStore.setRefresh(refresh);
    return !!token;
  };

  // Atomically commit the post-login state — the welcome splash text/route
  // AND the `authed=true` flag get set in the same synchronous block so
  // React 18 batches them into a SINGLE render.  On that render:
  //   • `welcome` is truthy → PublicOnly / RequireAuth return null
  //   • WelcomeGate renders the full-screen splash
  //   • When the splash finishes, its onFinish handler navigates to `next`
  // No route Guard ever sees `authed=true` with stale onboarding state.
  const commitLoginWithWelcome = useCallback((payload, freshStatus) => {
    const nextStep = getNextOnboardingStep(freshStatus);
    const isNew =
      payload?.is_new_user === true ||
      payload?.next_step === "onboarding" ||
      payload?.next_step === "profile_basics" ||
      (!freshStatus ||
        freshStatus?.completed !== true &&
        freshStatus?.is_complete !== true &&
        freshStatus?.onboarding_complete !== true &&
        !!nextStep);
    const welcomeText = isNew ? "Welcome to BrontoY" : "Welcome back";
    // Optional post-login redirect override — used by the /help/delete
    // flow to send the user to the deletion confirmation screen after
    // authenticating (instead of /home). Consumed one time.
    let override = null;
    try {
      override = sessionStorage.getItem("brontoy_post_login_redirect");
      if (override) sessionStorage.removeItem("brontoy_post_login_redirect");
    } catch {
      /* storage disabled */
    }
    const defaultRoute = nextStep ? `/onboarding/${nextStep}` : "/home";
    const nextRoute = override || defaultRoute;
    setWelcome({ text: welcomeText, next: nextRoute });
    setWelcome({ text: welcomeText, next: nextRoute });
    setAuthed(true);
  }, []);

  // ============================================================
  // NEW SIGNUP FLOW (preferred — /account_verification/request + confirm)
  // ============================================================

  /**
   * Step 1 — request OTP for a new account.
   * We hold `password` + `username` in memory / sessionStorage until the OTP
   * is confirmed (Xano's /confirm endpoint now requires username too).
   */
  const requestAccountVerification = async ({ email, password, username, terms_accepted, privacy_accepted }) => {
    const res = await api.post(EP.accountVerificationRequest, { email });
    signupSessionStore.set({ email, password, username, terms_accepted, privacy_accepted });
    tokenStore.setPendingEmail(email);
    return unwrap(res.data);
  };

  /**
   * Step 2 — confirm OTP with the pending password + username. Creates the
   * user + their profile and returns tokens.
   * Also supports supplying a password explicitly (used when arriving from
   * login for an unverified account — the signup session was never populated).
   */
  const confirmAccountVerification = async (otp, overridePassword) => {
    const pending = signupSessionStore.get() || {
      email: signupSessionStore.getEmail(),
      username: signupSessionStore.getUsername(),
    };
    const email = pending?.email || tokenStore.getPendingEmail();
    const username = pending?.username || signupSessionStore.getUsername();
    if (!email) {
      throw new Error("No signup in progress. Please start over.");
    }
    if (!username) {
      const e = new Error("Your signup session expired. Please start over from the signup page.");
      e.needsRestart = true;
      throw e;
    }
    const password = overridePassword || pending?.password;
    if (!password) {
      const e = new Error("Your session expired. Please re-enter your password.");
      e.needsPassword = true;
      throw e;
    }
    const res = await api.post(EP.accountVerificationConfirm, {
      email,
      otp,
      password,
      username,
    });
    const payload = unwrap(res.data); // will throw if Xano returns a Throw Error envelope
    // Only persist and clear session AFTER we know we have an auth token
    const token = payload?.auth_token || payload?.authToken || payload?.token;
    if (!token) {
      // Success shape without a token — treat as failure
      throw new Error(payload?.message || "Verification failed. Please try again.");
    }
    persistAuthTokens(payload);
    if (payload?.user) setUser(payload.user);
    signupSessionStore.clear();
    const meResult = await fetchMe();
    commitLoginWithWelcome(payload, meResult?.status);
    return payload;
  };

  /**
   * Resend the OTP by re-calling the request endpoint.
   */
  const resendAccountVerification = async () => {
    const email = signupSessionStore.getEmail() || tokenStore.getPendingEmail();
    if (!email) throw new Error("No signup in progress.");
    const res = await api.post(EP.accountVerificationRequest, { email });
    return unwrap(res.data);
  };

  // ============================================================
  // LOGIN
  // ============================================================
  const login = async (email, password) => {
    const res = await api.post(EP.login, { email, password });
    const payload = unwrap(res.data);
    persistAuthTokens(payload);
    if (payload?.user) setUser(payload.user);
    // If Xano says the account still needs email verification, seed the pending
    // email so /verify can display a helpful message.
    if (payload?.next_step === "verify_email" || payload?.user?.email_verified === false) {
      tokenStore.setPendingEmail(email);
      // Do NOT flip authed / show welcome — the caller will route to
      // /verify manually.  Leave state as-is so PublicOnly keeps the
      // login page mounted until the caller navigates away.
      return payload;
    }
    const meResult = await fetchMe();
    commitLoginWithWelcome(payload, meResult?.status);
    return payload;
  };

  // ============================================================
  // GOOGLE SIGN-IN (web) — hands Xano the ID-token JWT from GSI.
  //   POST /auth/google/web  { id_token }
  //   → { auth_token, refresh_token?, user, next_step? }
  // Returns the raw Xano payload so the caller can route (new users
  // → onboarding, existing users → home) via next_step / is_new_user.
  // ============================================================
  const googleWebLogin = async (idToken) => {
    const res = await api.post(EP.authGoogleWeb, { id_token: idToken });
    const payload = unwrap(res.data);
    persistAuthTokens(payload);
    if (payload?.user) setUser(payload.user);
    const meResult = await fetchMe();
    commitLoginWithWelcome(payload, meResult?.status);
    return payload;
  };

  // ============================================================
  // APPLE SIGN-IN (web) — matches the OpenAPI spec at
  //   POST /api:brontoy-login-apple-google/auth/apple/web
  //
  //   Required request body (per OpenAPI spec):
  //     {
  //       code:            <string>,   // Apple authorization code (single-use)
  //       identity_token:  <string>,   // Apple id_token JWT (signed)
  //     }
  //
  //   200 response (per OpenAPI spec):
  //     {
  //       auth_token: <string>,
  //       user: {
  //         id, created_at, updated_at,
  //         email, username, email_verified,
  //         profile_completed,
  //         onboarding_status: "pending_email_verification" | "pending_profile" | "completed",
  //         account_status:    "pending_verification" | "active" | "suspended" | "banned",
  //         role, apple_sub, google_sub, auth_provider, ...
  //       }
  //     }
  //
  //   Xano decodes the identity_token JWT server-side to extract the
  //   user's name / email / Apple sub — we do NOT need to send those
  //   as separate fields (the spec doesn't declare them).
  // ============================================================
  const appleWebLogin = async (code, identityToken) => {
    if (!code) throw new Error("Apple sign-in did not return an authorization code.");
    if (!identityToken) throw new Error("Apple sign-in did not return an identity token.");
    const body = { code, identity_token: identityToken };
    console.log("[BrontoY Apple] POST /auth/apple/web  body =", {
      code_preview: `${String(code).slice(0, 12)}…${String(code).slice(-6)}`,
      code_length: String(code).length,
      identity_token_preview: `${String(identityToken).slice(0, 12)}…${String(identityToken).slice(-6)}`,
      identity_token_length: String(identityToken).length,
    });
    let res;
    try {
      res = await api.post(EP.authAppleWeb, body);
    } catch (netErr) {
      console.error("[BrontoY Apple] /auth/apple/web  NETWORK ERROR:", netErr);
      throw netErr;
    }
    console.log("[BrontoY Apple] /auth/apple/web  raw response =", res?.data);
    const payload = unwrap(res.data);
    console.log("[BrontoY Apple] /auth/apple/web  unwrapped payload =", payload);
    const token =
      payload?.auth_token ||
      payload?.authToken ||
      payload?.token ||
      payload?.accessToken;
    if (!token) {
      const embedded =
        payload?.message ||
        payload?.error ||
        payload?.payload ||
        (typeof payload === "string" ? payload : null);
      const err = new Error(
        embedded ||
          "Apple sign-in did not return an authentication token. Please try again."
      );
      err.xanoResponse = payload;
      console.error("[BrontoY Apple] /auth/apple/web  200 without auth_token:", payload);
      throw err;
    }
    persistAuthTokens(payload);
    if (payload?.user) setUser(payload.user);
    const meResult = await fetchMe();
    commitLoginWithWelcome(payload, meResult?.status);
    return payload;
  };

  // ============================================================
  // PASSWORD RESET (new flow with request → confirm → reset_token)
  // ============================================================
  const forgotPassword = async (email) => {
    const res = await api.post(EP.forgotPasswordRequest, { email });
    tokenStore.setPendingEmail(email);
    return unwrap(res.data);
  };

  /**
   * Confirm the reset OTP AND set the new password in one step.
   * Xano's /forgot-password/confirm accepts {email, otp, new_password} and completes the reset.
   */
  const confirmForgotPassword = async ({ email, otp, new_password }) => {
    const res = await api.post(EP.forgotPasswordConfirm, { email, otp, new_password });
    return unwrap(res.data);
  };

  // ============================================================
  // LOGOUT
  // ============================================================
  const logout = async () => {
    const rt = tokenStore.getRefresh();
    try {
      if (rt) await api.post(EP.logout, { refresh_token: rt, refreshToken: rt });
    } catch {
      /* ignore */
    }
    tokenStore.clearAll();
    signupSessionStore.clear();
    setUser(null);
    setOnboarding(null);
    setAuthed(false);
    window.location.href = "/";
  };

  const value = {
    user,
    onboarding,
    loading,
    authed,
    // new flow
    requestAccountVerification,
    confirmAccountVerification,
    resendAccountVerification,
    getPendingSignupEmail: () => signupSessionStore.getEmail() || tokenStore.getPendingEmail(),
    // login / reset / logout
    login,
    googleWebLogin,
    appleWebLogin,
    forgotPassword,
    confirmForgotPassword,
    logout,
    refresh: fetchMe,
    apiError,
    // Welcome splash — trigger from any auth-success callsite; the App
    // root renders WelcomeSplash whenever `welcome` is truthy.
    welcome,
    triggerWelcome,
    clearWelcome,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
